const SECTION_SELECTOR = '[data-impact-snapshot]';
const MOBILE_QUERY = window.matchMedia('(max-width: 1023px)');
const instances = new Map();

class ImpactSnapshot {
  constructor(section) {
    this.section = section;
    this.device = section.querySelector('[data-snapshot-device]');
    this.mediaItems = Array.from(section.querySelectorAll('[data-snapshot-media]'));
    this.triggers = Array.from(section.querySelectorAll('[data-snapshot-trigger]'));
    this.gsap = window.gsap || null;
    this.activeIndex = this.getInitialIndex();
    this.observer = null;
    this.animating = false;

    this.handleIntersect = this.handleIntersect.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);

    this.init();
  }

  getInitialIndex() {
    const active = this.mediaItems.find((item) => item.classList.contains('is-active'));

    if (active) {
      const index = parseInt(active.dataset.snapshotIndex, 10);
      return Number.isNaN(index) ? 0 : index;
    }

    return 0;
  }

  init() {
    if (!this.device || !this.mediaItems.length) {
      return;
    }

    this.setInitialState();

    if (this.mediaItems.length <= 1) {
      return;
    }

    if (!MOBILE_QUERY.matches) {
      this.createObserver();
    }

    MOBILE_QUERY.addEventListener('change', this.handleMediaChange);
  }

  handleMediaChange(event) {
    if (event.matches) {
      this.destroyObserver();
      this.resetMediaStyles();
      return;
    }

    this.setInitialState();
    this.createObserver();
  }

  setInitialState() {
    this.mediaItems.forEach((media, index) => {
      if (this.gsap) {
      this.gsap.set(media, {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        yPercent: 100,
        autoAlpha: 0,
        zIndex: 0,
      });
    } else { 
      media.style.position = 'absolute';
      media.style.top = '0';
      media.style.left = '0';
      media.style.width = '100%';
      media.style.height = '100%';
      media.style.transform = 'translateY(100%)';
      media.style.opacity = '0';
      media.style.zIndex = '0';
    }

      media.classList.toggle('is-active', index === this.activeIndex);
    });

    const activeMedia = this.mediaItems[this.activeIndex];
    if (!activeMedia) return;

    if (this.gsap) {
      this.gsap.set(activeMedia, { yPercent: 0, autoAlpha: 1 });
    } else {
      activeMedia.style.transform = 'translateY(0)';
      activeMedia.style.opacity = '1';
    }
  }

  createObserver() {
    this.destroyObserver();

    if (!('IntersectionObserver' in window)) {
      return;
    }

    this.observer = new IntersectionObserver(this.handleIntersect, {
      rootMargin: '-25% 0px -65% 0px',
      threshold: 0,
    });

    this.triggers.forEach((trigger) => this.observer.observe(trigger));
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const targetIndex = parseInt(entry.target.dataset.snapshotTarget, 10);
      const nextIndex = Number.isNaN(targetIndex) ? this.activeIndex : targetIndex;

      this.animateToIndex(nextIndex);
    });
  }

  animateToIndex(nextIndex) {
    if (this.animating || nextIndex === this.activeIndex) {
      return;
    }

    const incoming = this.mediaItems.find((item) => parseInt(item.dataset.snapshotIndex, 10) === nextIndex);
    const outgoing = this.mediaItems.find((item) => item.classList.contains('is-active'));
    const direction = nextIndex > this.activeIndex ? 1 : -1;

    if (!incoming || incoming === outgoing) {
      this.activeIndex = nextIndex;
      return;
    }

    this.animating = true;

    if (this.gsap) {
      this.gsap.set(incoming, { yPercent: direction * 100, autoAlpha: 1, zIndex: 2 });
      if (outgoing) this.gsap.set(outgoing, { zIndex: 1 });

      this.gsap.to(outgoing, {
        yPercent: -direction * 50,
        duration: 0.6,
        ease: 'power2.out',
      });

      this.gsap.to(incoming, {
        yPercent: 0,
        autoAlpha: 1,
        duration: 0.6,
        ease: 'power2.out',
        onComplete: () => {
          this.finishAnimation(incoming, outgoing, nextIndex);
        },
      });
    } else {
      incoming.style.transform = `translateY(${direction * 100}%)`;
      incoming.style.opacity = '1';
      incoming.style.zIndex = '2';

      if (outgoing) {
        outgoing.style.zIndex = '1';
      }
      requestAnimationFrame(() => {
        incoming.style.transform = 'translateY(0)';
        incoming.style.opacity = '1';
        incoming.style.zIndex = '2';
        if (outgoing) {
          outgoing.style.transform = `translateY(${-direction * 50}%)`;
          outgoing.style.opacity = '1';
          outgoing.style.zIndex = '1';
        }

        this.finishAnimation(incoming, outgoing, nextIndex);
      });
    }
  }

  finishAnimation(incoming, outgoing, nextIndex) {
    this.mediaItems.forEach((item) => item.classList.remove('is-active'));
    incoming.classList.add('is-active');

    if (this.gsap) {
      this.mediaItems.forEach((item) => {
        this.gsap.set(item, { clearProps: 'zIndex' });
        this.gsap.set(item, { zIndex: item === incoming ? 2 : 0 });
      });
      if (outgoing) this.gsap.set(outgoing, { clearProps: 'yPercent,autoAlpha,zIndex' });
    } else {
      if (outgoing) {
        outgoing.style.transform = 'translateY(100%)';
        outgoing.style.zIndex = '0';
      }
      this.mediaItems.forEach((item) => {
        item.style.zIndex = item === incoming ? '2' : '0';
      });
    }

    this.activeIndex = nextIndex;
    this.animating = false;
  }

  resetMediaStyles() {
    this.mediaItems.forEach((media) => {
      media.classList.remove('is-active');
      media.style.removeProperty('opacity');
      media.style.removeProperty('transform');
      media.style.removeProperty('position');
      media.style.removeProperty('top');
      media.style.removeProperty('left');
      media.style.removeProperty('width');
      media.style.removeProperty('height');
    });
  }

  destroyObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  destroy() {
    this.destroyObserver();
    MOBILE_QUERY.removeEventListener('change', this.handleMediaChange);
    this.resetMediaStyles();
  }
}

function initImpactSnapshots(root = document) {
  const sections = root.querySelectorAll(SECTION_SELECTOR);

  sections.forEach((section) => {
    if (instances.has(section)) return;
    instances.set(section, new ImpactSnapshot(section));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initImpactSnapshots());
} else {
  initImpactSnapshots();
}

document.addEventListener('shopify:section:load', (event) => {
  initImpactSnapshots(event.target || document);
});

document.addEventListener('shopify:section:unload', (event) => {
  const container = event.target || document;
  container.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
    const instance = instances.get(section);
    if (instance) {
      instance.destroy();
      instances.delete(section);
    }
  });
});
